import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { faBuilding, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { reassignActivity, reassignResource } from 'api/contacts/activities';
import { getSelectedContactLabel } from 'assets/js/pages/contacts/ContactForm';
import {
    getResourceType,
    getResourceTypeFriendly,
} from 'assets/js/pages/contacts/single/timeline/timeline-list/change-contact/helpers';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { SelectItemWithLabel, SelectListItem, UseFormSearchableSelect } from 'components/select';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useForceRender } from 'customHooks/useForceRender';
import { useInfiniteContacts } from 'customHooks/useInfiniteContacts';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface Activity {
    id?: number;
    type: string;
    source: string;
    resource: {
        id: number;
    };
}

interface ChangeContactProps {
    contact: Contact;
    activity: Activity;
    onSuccess?: Function;
    children: React.ReactNode;
}

interface ChangeContactFormValues {
    selected: Required<SelectItemWithLabel<Contact>>[];
}

const ChangeContact = ({ children, activity, onSuccess, contact }: ChangeContactProps) => {
    const contactId = contact.id;
    const { error } = useToast();
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const resourceTypeFriendly = getResourceTypeFriendly(activity);
    const queryClient = useQueryClient();

    const methods = Form.useForm<ChangeContactFormValues>({
        defaultValues: {
            selected: [],
        },
    });

    const { watch, reset, path } = methods;
    const selected = watch('selected')[0];
    const selectedContact = selected?.value;

    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            if (activity.source === 'manual' && activity.id && selectedContact) {
                return reassignActivity(accountId, contactId, {
                    newContactId: selectedContact.id,
                    activityId: activity.id,
                });
            } else if (activity.source === 'system' && selectedContact) {
                return reassignResource(accountId, contactId, {
                    newContactId: selectedContact.id,
                    resourceId: activity.resource?.id,
                    resourceType: getResourceType(activity),
                });
            }

            return Promise.reject();
        },
        onSuccess: () => {
            onSuccess?.();
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });

            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tickets(accountId) });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to reassign resource'),
    });

    const handleAssignContact = useCallback(() => {
        if (!selected || !contactId) {
            return;
        }
        mutate();
    }, [selected, contactId, mutate]);

    const contactDisplayName =
        contact.type === ContactType.Company ? contact.company_name : contact.name_display;

    const selectedDisplayName = useMemo(() => {
        if (!selectedContact) return 'contact';

        return selectedContact.type === ContactType.Company
            ? selectedContact.company_name
            : selectedContact.name_display;
    }, [selectedContact]);

    return (
        <Dialog.Root key={key} onOpenChange={() => reset()}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="rounded-lg bg-white p-6 shadow-lg">
                    <Form.Provider {...methods}>
                        <Dialog.Description className="flex flex-col gap-4">
                            <p className="text-2xl">Assign {resourceTypeFriendly}</p>
                            <p>
                                This {resourceTypeFriendly} is currently associated with{' '}
                                <b>{contactDisplayName}</b>. To reassign it, select a different
                                contact below.
                            </p>
                        </Dialog.Description>
                        <Dialog.Body>
                            <Form.Container>
                                <Form.Label name={path('selected')}>Assigned contact</Form.Label>
                                <Suspense
                                    fallback={
                                        <Box className="relative h-10">
                                            <Loader />
                                        </Box>
                                    }
                                >
                                    <ContactSelection />
                                </Suspense>
                            </Form.Container>
                            <Button
                                variant="solid"
                                onClick={handleAssignContact}
                                disabled={!selected || isPending}
                            >
                                Assign to {selectedDisplayName}
                            </Button>
                        </Dialog.Body>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ContactSelection = () => {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { data: contactsData, ref: contactsRef } = useInfiniteContacts({
        query: debouncedSearch,
    });
    const contacts = contactsData?.pages;

    const { watch, reset, control } = Form.useFormContext<ChangeContactFormValues>();
    const selected = watch('selected')[0];
    const selectedContact = selected?.value;

    return selectedContact ? (
        <Box className="flex justify-between p-3">
            <ContactItem contact={selectedContact} />
            <Button variant="none" color="neutral" onClick={() => reset()}>
                <FontAwesomeIcon icon={faTimes} />
            </Button>
        </Box>
    ) : (
        <UseFormSearchableSelect
            control={control}
            name="selected"
            localSearch={false}
            clearable={true}
            rules={{ required: 'Select a contact' }}
            inputProps={{
                placeholder: 'Select contact',
                onChange: e => {
                    setSearch(e.currentTarget.value);
                },
            }}
        >
            {contacts.map((item: Contact) => (
                <SelectListItem
                    key={item.id}
                    value={{
                        id: item.id.toString(),
                        label: getSelectedContactLabel(item),
                        value: item,
                    }}
                >
                    <ContactItem contact={item} />
                </SelectListItem>
            ))}
            <div ref={contactsRef} />
        </UseFormSearchableSelect>
    );
};

const ContactItem = ({ contact }: { contact: Contact }) => {
    return (
        <div className="flex flex-col gap-0 text-sm">
            <p>
                {contact.type === ContactType.Company ? (
                    <span className="flex items-center gap-1">
                        {contact.company_name}
                        <Badge size="sm" color="primary">
                            <FontAwesomeIcon icon={faBuilding} /> Company
                        </Badge>
                    </span>
                ) : (
                    contact.name_display
                )}
            </p>
            {contact.first_name && contact.primary_email && (
                <p className="font-normal">{contact.primary_email}</p>
            )}
        </div>
    );
};

const Root = ChangeContact;
const Trigger = Dialog.Trigger;
const ChangeContactTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    ChangeContact,
    ChangeContactTrigger,
};
